import {
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize, forkJoin, of } from 'rxjs';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { ConfigurationKeys, Language, Section, SectionType } from '@lemonsolutions/models';
import { UtilsService } from '@ls-commons/utils';
import { ModalService } from '@ls-commons/utils/modals';
import { FlagLanguagePipe, InfoLanguagePipe } from '@ls-commons/utils/pipes';
import {
  CheckboxAtomComponent,
  FormFieldMoleculeComponent,
  LabelAtomComponent,
  CalendarAtomComponent,
  SingleUploadFileAtomComponent,
  CancelSaveMoleculeComponent
} from '@ls-commons/components';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TabMenuModule } from 'primeng/tabmenu';
import { TabView, TabViewChangeEvent, TabViewModule } from 'primeng/tabview';
import { EditorModule } from 'primeng/editor';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';

import { SectionService } from '../section.service';

@Component({
  selector: 'lib-edit-section',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormFieldMoleculeComponent,
    LabelAtomComponent,
    CheckboxAtomComponent,
    CalendarAtomComponent,
    SingleUploadFileAtomComponent,
    CancelSaveMoleculeComponent,
    FormsModule,
    FlagLanguagePipe,
    InfoLanguagePipe,

    TabMenuModule,
    TabViewModule,
    TagModule,
    EditorModule,
    TooltipModule,
  ],
  providers: [],
  templateUrl: './section-edit.component.html',
  styleUrl: './section-edit.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SectionEditComponent implements OnInit {
  languages!: Language[];
  sectionType!: SectionType;
  selectedIndex = 0;
  sectionId!: number;
  selectedLang!: string;
  title = '';
  saving = false;
  isSelectedMasterTab = false;

  date_ini = null;
  date_end = null;

  sectionForm!: FormGroup;
  sectionService = inject(SectionService);
  utilsService = inject(UtilsService);
  modalService = inject(ModalService);

  @ViewChild('tv') tabView!: TabView;
  @ViewChild('fileUpload') fileUpload!: SingleUploadFileAtomComponent;
  @ViewChild('dateIni') dateIni!: CalendarAtomComponent;
  @ViewChild('dateEnd') dateEnd!: CalendarAtomComponent;

  private _isLoading = signal<boolean>(true);
  public isLoading = computed(() => this._isLoading());

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    readonly fb: FormBuilder
  ) {
    const { sectionId, type, title, lang } = this.config.data;
    this.sectionType = type;
    this.title = title;
    this.sectionId = sectionId;
    this.selectedLang = lang;

    this.sectionForm = this.fb.group({
      datesChecked: [false],
      date_ini: [{ value: null, disabled: true }],
      date_end: [{ value: null, disabled: true }],
      sections: this.fb.array([]),
      images: this.fb.array([]),
    });
    this.addLanguageToForm(false, '', null);
  }

  get sections() {
    return this.sectionForm.get('sections') as FormArray;
  }

  section(index: number): FormGroup {
    return this.sections.controls[index] as FormGroup;
  }

  get images() {
    return this.sectionForm.get('images') as FormArray;
  }

  image(index: number): FormGroup {
    return this.images.controls[index] as FormGroup;
  }

  addLanguageToForm(master: boolean, lang: string, order: number | null) {
    const section = this.fb.group({
      id: [this.sectionId],
      type: [this.sectionType.id],
      lang: [lang],
      order: [order],
      master: [master],
      title: ['', master && Validators.required],
      description: [''],
      image: [null],
      active: [false],
      date_ini: [null],
      date_end: [null],
    });
    this.sections.push(section);
    const image = this.fb.group({
      lang: [lang],
      name: [null],
      file: [null],
    });
    this.images.push(image);
  }

  initializeFormWithLanguages(languages: Language[], order: number | null) {
    this.sections.clear();
    this.images.clear();
    this.languages = languages;
    languages.forEach(lang => {
      this.addLanguageToForm(lang.master, lang.lang, order);
    });
  }

  async processSections(languages: Language[], sections: Section[]) {
    const upload_root_url = await this.utilsService.getFrontConfiguration(ConfigurationKeys.UPLOAD_ROOT_URL) || '';

    this.initializeFormWithLanguages(
      languages,
      sections.length > 0 ? sections[0].order : null
    );

    this.sectionForm.controls['date_ini'].setValue(sections[0].date_ini);
    this.sectionForm.controls['date_end'].setValue(sections[0].date_end);
    this.sectionForm.controls['datesChecked'].setValue(
      sections[0].date_ini !== null
    );
    this.clickEnableDates(sections[0].date_ini !== null);

    for (let index = 0; index < sections.length; index++) {
      const sectionForm = this.sections.controls.find(
        s => s.value.lang === sections[index].lang
      );

      sectionForm && sectionForm.patchValue(sections[index]);
      const imageForm = this.images.controls.find(
        s => s.value.lang === sections[index].lang
      );
      if (imageForm && sections[index].image) {
        const image = await this.utilsService
          .getImageFileFromUrl(sections[index].image, upload_root_url)
          .catch(err => this.modalService.showErrorMessage(err));
        (imageForm as FormGroup).controls['file'].setValue(image);
      }
    }

    this.selectedIndex = languages.findIndex(l => l.lang === this.selectedLang);
    this.tabView?.cd.detectChanges();
    this.showFileUploadImage(this.selectedIndex);
  }

  ngOnInit(): void {
    const observableSection$ =
      this.sectionId != null
        ? this.sectionService.getSection(this.sectionType.id, this.sectionId)
        : of([]);

    forkJoin([this.utilsService.getLanguages(), observableSection$])
      .pipe(finalize(() => this._isLoading.set(false)))
      .subscribe({
        next: ([languages, response]) => {
          this.processSections(languages, response);
        },
        error: () => this.ref.close()
      });
  }

  closeDialog() {
    this.ref.close(false);
  }

  saveSection() {
    this.sections.controls.forEach(ctrl => {
      const datesChecked = this.sectionForm.get('datesChecked')?.value;
      (ctrl as FormGroup)?.controls['date_ini'].setValue(
        datesChecked ? this.sectionForm.get('date_ini')?.value : null
      );
      (ctrl as FormGroup)?.controls['date_end'].setValue(
        datesChecked ? this.sectionForm.get('date_end')?.value : null
      );
    });

    const section = this.sectionForm.getRawValue();

    const observable$ = this.sectionId
      ? this.sectionService.saveSection(
          this.sectionType.id,
          this.sectionId,
          section.sections,
          section.images
        )
      : this.sectionService.addSection(
          this.sectionType.id,
          section.sections,
          section.images
        );
    this.sectionForm.markAllAsTouched();

    if (this.sectionForm.valid) {
      this.saving = true;
      observable$.pipe(finalize(() => (this.saving = false))).subscribe({
        next: () => {
          this.modalService.showSuccessMessage(
            `${this.title} ${!this.sectionId ? 'añadido' : 'modificado'}`
          );
          this.ref.close(true);
        },
      });
    } else {
      this.selectedIndex = 0;
      this.tabView.cd.detectChanges();
    }
  }

  onFileSelect(event: File) {
    this.image(this.selectedIndex).get('file')?.setValue(event);
    this.section(this.selectedIndex)
      .get('image')
      ?.setValue(event.name);
  }

  onFileRemove(event: File) {
    this.image(this.selectedIndex).get('file')?.setValue(null);
    this.section(this.selectedIndex).get('image')?.setValue(null);
  }

  showFileUploadImage(index: number) {
    this.isSelectedMasterTab = this.section(index).value.master;
    const image = this.image(index).get('file')?.value;
    this.fileUpload.files = image ? [image] : [];
  }

  changeLang(event: TabViewChangeEvent) {
    this.showFileUploadImage(event.index);
  }

  clickEnableDates(event: boolean) {
    ['date_ini', 'date_end'].forEach(name => {
      const date = this.sectionForm.controls[name];
      date.setValidators(event ? Validators.required : null);
      !event ? date.disable() : date.enable();
    });
  }
}
